import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { UPLOAD_TEMPLATE_URL, get_auth_headers, get_request_config, GET_DOCUMENT_TYPES } from '@/utils/system_constants/constant';
import { validate_file, validate_non_empty_string, get_error_message } from '@/utils/mixins/validate_inputs';

export default {
  name: 'UploadTemplate',
  mixins: [alert_mixin],
  data() {
    return {
      selected_file: null,
      template_name: '',
      template_description: '',
      template_types: [],
      selected_template_type: '',
      preview_url: null,
      is_previewable: false,
      upload_loading: false,
    };
  },
  mounted() {
    this.fetch_template_types();
  },
  beforeUnmount() {
    this.clear_file_selection();
  },
  methods: {
    go_back() {
      this.$router.push({ name: 'ViewTemplate' });
    },
    fetch_template_types() {
      return axios
        .get(GET_DOCUMENT_TYPES, get_request_config())
        .then((response) => {
          const raw = response?.data?.data || [];
          const list = Array.isArray(raw) ? raw : [];

          this.template_types = list
            .map((item) => {
              if (typeof item === 'string') return { value: item, label: item };

              const value = item?.value ?? item?.id ?? item?.type ?? item?.template_type;
              const label = item?.label ?? item?.template_type ?? item?.type ?? value;
              if (value == null) return null;
              return { value: String(value), label: String(label ?? value) };
            })
            .filter(Boolean);

          return this.template_types;
        })
        .catch((error) => {
          this.template_types = [];
          this.show_error(get_error_message(error) || 'Could not load template types. Please refresh.');
          return [];
        });
    },
    async handle_file_selection(evt) {
      const file = evt?.target?.files?.[0];
      if (!file) {
        this.clear_file_selection();
        return;
      }

      const extension = String(file.name || '').split('.').pop().toLowerCase();
      if (extension !== 'pdf') {
        this.show_error('Only PDF files are allowed');
        this.clear_file_selection();
        if (evt?.target) evt.target.value = '';
        return;
      }

      const validation = await validate_file(file);
      if (!validation.valid) {
        this.show_error(validation.error);
        this.clear_file_selection();
        if (evt?.target) evt.target.value = '';
        return;
      }

      this.selected_file = file;
      this.handle_file_preview(file);
    },
    clear_file_selection() {
      if (this.preview_url) URL.revokeObjectURL(this.preview_url);
      this.preview_url = null;
      this.is_previewable = false;
      this.selected_file = null;
    },
    handle_file_preview(file) {
      const previewable_types = ['pdf'];
      const extension = String(file?.name || '').split('.').pop().toLowerCase();
      this.is_previewable = previewable_types.includes(extension);
      this.preview_url = URL.createObjectURL(file);
    },
    reset_upload_form() {
      this.clear_file_selection();
      this.template_name = '';
      this.template_description = '';
      this.selected_template_type = '';
      this.upload_loading = false;
    },
    async upload_template() {
      if (!this.selected_file) {
        this.show_error('Please select a file to upload');
        return;
      }
      if (!validate_non_empty_string(this.template_name)) {
        this.show_error('Please enter a valid template name');
        return;
      }
      if (!validate_non_empty_string(this.template_description)) {
        this.show_error('Please enter a valid template description');
        return;
      }
      if (!validate_non_empty_string(this.selected_template_type)) {
        this.show_error('Please select a template type');
        return;
      }

      this.upload_loading = true;

      const form_data = new FormData();
      form_data.append('template', this.selected_file);
      form_data.append('template_name', this.template_name);
      form_data.append('template_description', this.template_description);
      form_data.append('template_type', this.selected_template_type);

      try {
        await axios.post(UPLOAD_TEMPLATE_URL, form_data, {
          withCredentials: true,
          headers: { ...get_auth_headers() },
        });

        this.show_success('Template uploaded successfully');
        this.reset_upload_form();
        this.$router.push({ name: 'ViewTemplate' });
      } catch (error) {
        this.show_error(get_error_message(error) || 'Upload failed');
      } finally {
        this.upload_loading = false;
      }
    },
  },
};
